import { useState, useContext, useEffect, useCallback, useMemo } from 'react'
import Fuse from 'fuse.js';
import WizardDataContext from '../../../store/WizardDataContext';

import { FormGroup, Accordion, AccordionDetails, AccordionSummary, FormControlLabel, Checkbox, CircularProgress, TextField } from "@mui/material";
import { ExpandMoreRounded, Search } from '@mui/icons-material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export default function Categories({ setDisableBtn }) {

  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;
  const categoriesData = wizardData.categories;
  const selectedDeal = wizardData.dealSelection?.selectedDeal;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Memoize selected categories for faster lookups
  const selectedCategoriesSet = useMemo(() => {
    return new Set(categoriesData.selected?.map(cat => cat.id) || []);
  }, [categoriesData.selected]);

  useEffect(() => {
    // Check if at least 1 category is selected
    const hasEnoughCategories = categoriesData.selected && categoriesData.selected.length >= 1;
    setDisableBtn(!hasEnoughCategories);
  }, [categoriesData.selected, setDisableBtn])

  useEffect(() => {
    getCategories();
  }, [])

  // Optimized function to check if a category is selected
  const isCategorySelected = useCallback((categoryId) => {
    return selectedCategoriesSet.has(categoryId);
  }, [selectedCategoriesSet]);

  // Optimized function to add category
  const addCategory = useCallback((item) => {
    const currentCategories = categoriesData.selected || [];
    if (!currentCategories.find(cat => cat.id === item.id)) {
      console.log('Adding category:', item.title, 'Current categories:', currentCategories.length);
      updateData('categories', { selected: [...currentCategories, item] });
    }
  }, [categoriesData.selected, updateData]);

  // Optimized function to remove category
  const removeCategory = useCallback((item) => {
    const currentCategories = categoriesData.selected || [];
    const updatedCategories = currentCategories.filter(cat => cat.id !== item.id);
    console.log('Removing category:', item.title, 'Updated categories:', updatedCategories.length);
    updateData('categories', { selected: updatedCategories });
  }, [categoriesData.selected, updateData]);

  // Optimized checkbox handler with immediate feedback
  const handleCheckboxChange = useCallback((e, item) => {
    const isChecked = e.target.checked;
    console.log('Checkbox changed:', item.title, 'Checked:', isChecked);
    
    if (isChecked) {
      addCategory(item);
    } else {
      removeCategory(item);
    }
  }, [addCategory, removeCategory]);

  // Handle close icon click
  const handleCloseClick = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    removeCategory(item);
  }, [removeCategory]);

  const getCategories = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_API_URI}/v3/categories`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json()

      if (data.status == 'success') {
        // Filter out categories with no subcategories
        const categoriesWithSubcategories = data.data.filter(category => 
          category.subCategories && category.subCategories.length > 0
        );
        
        // Sort parent categories alphabetically
        const sortedCategories = categoriesWithSubcategories.sort((a, b) => a.title.localeCompare(b.title));
        
        // Sort subcategories within each parent category alphabetically
        sortedCategories.forEach(category => {
          if (category.subCategories && category.subCategories.length > 0) {
            category.subCategories.sort((a, b) => a.title.localeCompare(b.title));
          }
        });
        
        setCategories(sortedCategories)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }
  }

  // Auto-select Niche Discounts subcategories based on chosen deal
  useEffect(() => {
    if (!categories || !categories.length || !selectedDeal) return;

    const dealToSubCategoryTitle = {
      student: 'Student Discounts',
      pensioner: 'Pensioner Discounts',
      signup: 'Signup Deals',
      birthday: 'Birthday Specials'
    };

    const targetSubCategoryTitle = dealToSubCategoryTitle[selectedDeal];
    if (!targetSubCategoryTitle) return;

    // Find the "Niche Discounts" parent category
    const nicheCategory = categories.find(cat => cat.title === 'Niche Discounts');
    if (!nicheCategory || !nicheCategory.subCategories || !nicheCategory.subCategories.length) return;

    // Find the matching subcategory under Niche Discounts
    const targetSubCategory = nicheCategory.subCategories.find(
      sub => sub.title === targetSubCategoryTitle
    );

    if (!targetSubCategory) return;

    const currentSelected = categoriesData.selected || [];
    const alreadySelected = currentSelected.some(cat => cat.id === targetSubCategory.id);
    if (alreadySelected) return;

    // Append the auto-selected niche subcategory
    updateData('categories', { selected: [...currentSelected, targetSubCategory] });
  }, [categories, categoriesData.selected, selectedDeal, updateData]);

  // Memoize subcategory rendering to prevent unnecessary re-renders
  const renderSubCategory = useCallback((item) => (
    <FormControlLabel 
      key={item.id} 
      control={
        <Checkbox 
          checked={isCategorySelected(item.id)} 
          onChange={(e) => handleCheckboxChange(e, item)} 
          value={item.title} 
        />
      } 
      label={item.title} 
    />
  ), [isCategorySelected, handleCheckboxChange]);

  const getSubCategory = useCallback((sub) => {
    return sub.map((item) => renderSubCategory(item));
  }, [renderSubCategory]);

  // Fuzzy search: only run when 2+ characters
  const effectiveSearchTerm = searchTerm.trim().length >= 2 ? searchTerm.trim() : '';

  // Memoize Fuse instance for fuzzy search (searches parent title + subcategory titles)
  const fuse = useMemo(() => {
    const withSubs = (categories || []).filter(c => c.subCategories?.length > 0);
    if (!withSubs.length) return null;
    return new Fuse(withSubs, {
      keys: ['title', 'subCategories.title'],
      threshold: 0.4,
      ignoreLocation: true,
    });
  }, [categories]);

  // Memoize the categories rendering with search filtering
  const renderedCategories = useMemo(() => {
    if (!categories?.length) return null;

    const withSubs = categories.filter(item => item.subCategories?.length > 0);
    let filteredCategories;

    if (!effectiveSearchTerm) {
      filteredCategories = withSubs;
    } else if (fuse) {
      const results = fuse.search(effectiveSearchTerm);
      filteredCategories = results.map(r => r.item);
    } else {
      filteredCategories = withSubs;
    }

    return filteredCategories.slice(0, 100).map((item, index) => (
      <Accordion key={item.id || index}>
        <AccordionSummary
          expandIcon={<ExpandMoreRounded />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <span>{item.title}</span>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            <FormGroup>
              {getSubCategory(item.subCategories)}
            </FormGroup>
          </div>
        </AccordionDetails>
      </Accordion>
    ));
  }, [categories, effectiveSearchTerm, fuse, getSubCategory]);

  // Memoize selected categories display with close icons
  const selectedCategoriesDisplay = useMemo(() => {
    if (!categoriesData.selected?.length) return null;
    
    return (
      <div className="selected-categories">
        {categoriesData.selected.sort((a, b) => a.title.localeCompare(b.title)).map((item, i) => (
          <div 
            className='categories' 
            key={item.id || i}
          > 
            <span>{item.title}</span>
            <CloseRoundedIcon 
              onClick={(e) => handleCloseClick(e, item)}
              style={{ 
                cursor: 'pointer', 
                marginLeft: '8px',
                fontSize: '16px',
                color: '#666'
              }}
            />
          </div>
        ))}
      </div>
    );
  }, [categoriesData.selected, handleCloseClick]);

  if (loading) {
    return (
      <div className="container">
        <div className="step8-container">
          <div className="step8">
            <div className="heading">
              <h1>Categories</h1>
              <h3>You can search for the relevant categories below</h3>
            </div>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}>
              <CircularProgress size={40} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container">
        <div className="step8-container">
          <div className="step8">
            <div className="heading">
              <h1>Select at Least One Relevant Category for Your Offer</h1>
              <h3>You can search for the relevant categories below</h3>
            </div>
            
            {selectedCategoriesDisplay}

            {/* Search Bar - same width as category box */}
            <div className="categories-search-wrap">
              <TextField
                fullWidth
                placeholder="Search Categories"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search style={{ marginRight: '8px', color: '#666' }} />,
                  style: { backgroundColor: 'white' }
                }}
                variant="outlined"
                size="small"
              />
            </div>
            
            <div className="select-categories">
              { effectiveSearchTerm && renderedCategories && renderedCategories.length === 0 ? <div className="no-results">No results found</div> : renderedCategories}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
